/**
 * cart-count-sync.js
 * ------------------------------------------------------------------
 * Single source of truth for the cart badge across the whole theme.
 *
 * cart.js already calls window.updateCartCountUI() and
 * window.refreshCartCountUI() in five places, but nothing ever defined
 * them - so every one of those calls was a silent no-op. This defines
 * them, and keeps BOTH badge styles in sync:
 *
 *   .sbl-cart__count      -> the custom SBL header badge
 *   .cart-count-bubble    -> Dawn's stock badge
 *
 * Both are created on the fly if they aren't in the DOM yet, which is
 * what happens when the cart starts empty (Liquid only renders them
 * when cart.item_count > 0).
 *
 * Loaded from layout/theme.liquid before cart.js and luxury-card.js.
 */
(() => {
  if (window.cartCountSyncInitialised) return;
  window.cartCountSyncInitialised = true;

  const cartUrl = `${window.Shopify?.routes?.root || '/'}cart.js`;

  /** Custom SBL header badge - create it if the cart started empty. */
  const syncSblBadge = (count) => {
    document.querySelectorAll('.sbl-cart').forEach((link) => {
      let badge = link.querySelector('.sbl-cart__count');

      if (!badge && count > 0) {
        badge = document.createElement('span');
        badge.className = 'sbl-cart__count';
        link.appendChild(badge);
      }

      if (!badge) return;

      badge.textContent = count;
      badge.hidden = count === 0;
      badge.style.display = count === 0 ? 'none' : '';
    });
  };

  /** Dawn's stock bubble - same deal. */
  const syncDawnBubble = (count) => {
    const holders = document.querySelectorAll(
      '#cart-icon-bubble, .header__icon--cart, [data-cart-bubble-holder]'
    );

    holders.forEach((holder) => {
      let bubble = holder.querySelector('.cart-count-bubble');

      if (!bubble && count > 0) {
        bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        bubble.innerHTML = '<span aria-hidden="true"></span>';
        holder.appendChild(bubble);
      }

      if (!bubble) return;

      const visibleSpan =
        bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span');

      if (visibleSpan) visibleSpan.textContent = count < 100 ? count : '';

      bubble.classList.toggle('hidden', count === 0);
    });

    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
    });
  };

  /**
   * Called by cart.js and luxury-card.js with a known count.
   * Cheap - no network request.
   */
  window.updateCartCountUI = (count) => {
    const total = Number(count) || 0;

    syncSblBadge(total);
    syncDawnBubble(total);

    document.documentElement.classList.toggle('cart-is-empty', total === 0);
  };

  /**
   * Called when the count isn't known locally. Hits /cart.js once.
   */
  window.refreshCartCountUI = async () => {
    try {
      const response = await fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return;

      const cart = await response.json();
      window.updateCartCountUI(cart.item_count);
      return cart;
    } catch (error) {
      console.warn('[cart-count-sync] Could not refresh cart count.', error);
    }
  };

  /* --------------------------------------------------------------
     Listen for every flavour of cart update the theme emits, so the
     badge stays correct no matter which code path did the work.
     -------------------------------------------------------------- */

  const readCount = (detail) => {
    const data = detail?.cartData || detail?.cart || detail;
    if (data && typeof data.item_count === 'number') return data.item_count;
    return null;
  };

  const wire = () => {
    // Dawn's pub/sub (published by cart.js, product-form.js, cart-drawer.js)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const count = readCount(event);
        if (count === null) {
          window.refreshCartCountUI();
        } else {
          window.updateCartCountUI(count);
        }
      });
    }

    // Legacy custom event kept for backwards compatibility
    document.addEventListener('cart:updated', (event) => {
      const count = readCount(event.detail);
      if (count !== null) window.updateCartCountUI(count);
    });

    // Back/forward cache restores a stale page - resync on show
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) window.refreshCartCountUI();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', wire, { once: true });
  } else {
    wire();
  }
})();
